import React, { ReactElement } from "react"
import { useStateMachine } from "../../../context/stateMachine/StateMachineV2"
import { Form } from "@carrotfertility/carotene-core-x"
import {
  useCheckRoutingInfoQueryCache,
  useUpdateRoutingInfo
} from "#/components/carrot-plans/shared/useQueryRoutingInfo"
import { defineMessage, FormattedMessage, useIntl } from "react-intl"
import {
  ButtonFooterLayout,
  ContinueButton,
  RoutingFlowTitle,
  StepLayout,
  useConvertTranslatedLabel
} from "#/components/carrot-plans/shared"
import { FormRadioButtonGroup } from "#/services/common-forms"
import { useCalibratedEngagementEnabled } from "#/hooks/calibratedEngagement/useCalibratedEngagement"
import { CenteredLoadingIndicator } from "#/components/views/molecules/Molecules"
import { AmabAfabMedicalPreference, MedicalQuestionsWorkflow } from "../medical-questions/medicalFlowConstants"

const amabAfabMedicalPreferenceOptions = [
  {
    label: defineMessage({ defaultMessage: "Yes, I'd like to answer questions related to ovarian/uterine health" }),
    workflow: MedicalQuestionsWorkflow.AFAB_MEDICAL_QUESTIONS,
    value: AmabAfabMedicalPreference.AFAB
  },
  {
    label: defineMessage({ defaultMessage: "Yes, I'd like to answer questions related to sperm health" }),
    workflow: MedicalQuestionsWorkflow.AMAB_MEDICAL_QUESTIONS,
    value: AmabAfabMedicalPreference.AMAB
  },
  {
    label: defineMessage({ defaultMessage: "No, I'd prefer not to answer health questions right now" }),
    value: AmabAfabMedicalPreference.DECLINE_MEDICAL_QUESTIONS
  }
]

const amabAfabMedicalPreferenceAlternateOptions = [
  {
    label: defineMessage({ defaultMessage: "Questions related to ovarian/uterine health" }),
    workflow: MedicalQuestionsWorkflow.AFAB_MEDICAL_QUESTIONS,
    value: AmabAfabMedicalPreference.AFAB
  },
  {
    label: defineMessage({ defaultMessage: "Questions related to sperm health" }),
    workflow: MedicalQuestionsWorkflow.AMAB_MEDICAL_QUESTIONS,
    value: AmabAfabMedicalPreference.AMAB
  }
]

export default function AmabAfabMedicalPreferenceStep(): ReactElement {
  const { mutateAsync } = useUpdateRoutingInfo()
  const routingData = useCheckRoutingInfoQueryCache()
  const calibratedEngagementQuery = useCalibratedEngagementEnabled()
  const defaultValue = routingData?.amabAfabMedicalPreference
  const { send } = useStateMachine(defaultValue ? "" : null)
  const intl = useIntl()
  const options = calibratedEngagementQuery.data
    ? amabAfabMedicalPreferenceAlternateOptions
    : amabAfabMedicalPreferenceOptions
  const radioOptions = useConvertTranslatedLabel(options)

  async function onClickContinue(formValues: { amabAfabMedicalPreference: string }): Promise<void> {
    await mutateAsync(formValues, { onSuccess: () => send("") })
  }
  const header = calibratedEngagementQuery.data
    ? intl.formatMessage({
        defaultMessage: "Next, we'll ask some health questions to help us guide you to the best care."
      })
    : intl.formatMessage({
        defaultMessage: "Would you like to answer some health questions to help us support you better?"
      })

  return calibratedEngagementQuery.isLoading ? (
    <CenteredLoadingIndicator />
  ) : (
    <>
      <RoutingFlowTitle title={header} />
      <StepLayout
        header={<span id="amabAfabMedicalPreference-label">{header}</span>}
        description={intl.formatMessage({
          defaultMessage: "Please select what's most relevant to your journey right now."
        })}
        formElements={
          <Form defaultValues={{ amabAfabMedicalPreference: defaultValue }} onSubmit={onClickContinue}>
            <FormRadioButtonGroup
              name="amabAfabMedicalPreference"
              options={radioOptions}
              aria-labelledby="amabAfabMedicalPreference-label"
              controllerProps={{ rules: { required: intl.formatMessage({ defaultMessage: "Required" }) } }}
            />
            <ButtonFooterLayout
              continueButton={
                <ContinueButton>
                  <FormattedMessage defaultMessage="Continue" />
                </ContinueButton>
              }
            />
          </Form>
        }
      />
    </>
  )
}
